import { getAuthToken, getCookie } from './cookies';
import { getPortalUserId, getPortalUserName } from './portalSession';

const JWT_IDENTITY_CLAIM_KEYS = [
  'name',
  'preferred_username',
  'email',
  'upn',
  'unique_name',
  'given_name',
  'family_name',
  'sub',
] as const;

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  if (!token || token.split('.').length !== 3) {
    return null;
  }
  try {
    const payload = token.split('.')[1];
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    const padded = pad ? b64 + '='.repeat(4 - pad) : b64;
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function tryJwtDisplayName(token: string): string | null {
  const json = decodeJwtPayload(token);
  if (!json) {
    return null;
  }
  for (const key of JWT_IDENTITY_CLAIM_KEYS) {
    const v = json[key];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return null;
}

function addUserMatchKey(keys: Set<string>, value: string | null | undefined): void {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) {
    return;
  }
  keys.add(raw);
  for (const part of raw.split(/[\s@._-]+/)) {
    if (part.length >= 2) {
      keys.add(part);
    }
  }
}

/**
 * All known identity strings for the current session (env, cookies, JWT claims).
 * Used to match tracker `user` / `userName` to the logged-in user.
 */
export function getLoggedInUserMatchKeys(): string[] {
  const keys = new Set<string>();
  addUserMatchKey(keys, getPortalUserName());
  addUserMatchKey(keys, getPortalUserId());
  addUserMatchKey(keys, (import.meta.env.VITE_USER_NAME as string | undefined)?.trim());
  addUserMatchKey(keys, getCookie('userName'));
  addUserMatchKey(keys, getCookie('user_name'));
  addUserMatchKey(keys, getCookie('displayName'));
  addUserMatchKey(keys, getCookie('name'));
  addUserMatchKey(keys, getCookie('email'));
  const json = decodeJwtPayload(getAuthToken());
  if (json) {
    for (const key of JWT_IDENTITY_CLAIM_KEYS) {
      const v = json[key];
      if (typeof v === 'string') {
        addUserMatchKey(keys, v);
      }
    }
  }
  return [...keys];
}

/**
 * Display name for greetings and UI.
 * Priority: portal projectsInfo.userName → VITE_USER_NAME → cookies → JWT claims.
 */
export function getDisplayUserName(): string | null {
  const fromPortal = getPortalUserName();
  if (fromPortal) {
    return fromPortal;
  }
  const env = (import.meta.env.VITE_USER_NAME as string | undefined)?.trim();
  if (env) {
    return env;
  }
  const fromCookie =
    getCookie('userName') ||
    getCookie('user_name') ||
    getCookie('displayName') ||
    getCookie('name');
  if (fromCookie?.trim()) {
    return fromCookie.trim();
  }
  const fromJwt = tryJwtDisplayName(getAuthToken());
  if (fromJwt) {
    return fromJwt;
  }
  return null;
}

/** Role label for header and navigation drawer. */
export function getDisplayUserRole(): string {
  return (import.meta.env.VITE_USER_ROLE as string | undefined)?.trim() || 'Admin';
}
